import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readDocument } from "@/office/word/document";

export const lektionSuchenErsetzen: Lesson = {
  id: "suchen-und-ersetzen",
  title: "Suchen und Ersetzen",
  summary: "Änderungen im ganzen Dokument in einem Zug – ohne etwas zu übersehen.",
  durationMinutes: 8,
  learningGoals: [
    "Text im Dokument finden und ersetzen",
    "Die Optionen für Groß-/Kleinschreibung und ganze Wörter nutzen",
    "Die Risiken von „Alle ersetzen“ einschätzen",
  ],
  steps: [
    {
      id: "ersetzen",
      title: "Ersetzen statt suchen und tippen",
      explanation: [
        "Ein Firmenname ändert sich, eine Abkürzung wird durchgängig ausgeschrieben, ein " +
          "Tippfehler zieht sich durchs ganze Dokument: Suchen und Ersetzen erledigt das " +
          "in einem Zug.",
        "Strg + F öffnet den Navigationsbereich mit der Suche, Strg + H den Dialog " +
          "„Suchen und Ersetzen“. Dort stehen zwei Schaltflächen nebeneinander: „Ersetzen“ " +
          "geht Fund für Fund vor und lässt dich jedes Mal entscheiden, „Alle ersetzen“ " +
          "macht alles auf einmal.",
        "„Alle ersetzen“ ist bequem und gefährlich. Ersetzt du „Art“ durch „Sorte“, " +
          "werden aus „Artikel“ plötzlich „Sortikel“. Deshalb: erst mit „Weitersuchen“ " +
          "ansehen, wie viele und welche Treffer es gibt.",
      ],
      keyPoints: [
        "Strg + F sucht, Strg + H öffnet Suchen und Ersetzen.",
        "„Ersetzen“ geht Schritt für Schritt, „Alle ersetzen“ auf einmal.",
        "Nach „Alle ersetzen“ meldet Word, wie viele Stellen geändert wurden.",
      ],
      task: {
        instruction:
          "Schreibe einen Text, in dem das Wort „Firma“ mindestens dreimal vorkommt. " +
          "Ersetze anschließend alle Vorkommen durch „Unternehmen“.",
        hint: "Strg + H, oben „Firma“, unten „Unternehmen“, dann „Alle ersetzen“.",
        check: async () => {
          const snapshot = await readDocument();
          const text = snapshot.text;
          if (text.trim().length === 0) {
            return fail("Im Dokument steht noch kein Text.", "Schreibe zuerst den Übungstext.");
          }

          const firma = (text.match(/Firma/gi) ?? []).length;
          const unternehmen = (text.match(/Unternehmen/gi) ?? []).length;

          return fromCriteria(
            [
              criterion(
                `Mindestens drei Mal „Unternehmen“ im Text (gefunden: ${unternehmen})`,
                unternehmen >= 3,
              ),
              criterion(`Kein „Firma“ mehr übrig (gefunden: ${firma})`, firma === 0),
            ],
            {
              success: "Sauber ersetzt – alle Vorkommen sind erwischt.",
              failure: "Die Ersetzung ist noch nicht vollständig.",
              hint: "Bleibt ein Vorkommen übrig, war vermutlich „Groß-/Kleinschreibung beachten“ aktiv. Die Option findest du über „Erweitern“ im Dialog.",
            },
          );
        },
      },
    },
    {
      id: "optionen",
      title: "Die Suchoptionen",
      explanation: [
        "Über „Erweitern“ klappt der Dialog auf und zeigt Optionen, die viele Fehlgriffe " +
          "verhindern.",
        "„Groß-/Kleinschreibung beachten“ trennt „Der“ von „der“. „Nur ganzes Wort " +
          "suchen“ verhindert genau das Sortikel-Problem aus dem letzten Schritt – „Art“ " +
          "findet dann nicht mehr „Artikel“. „Platzhalter verwenden“ öffnet die Tür zu " +
          "Mustersuchen für Fortgeschrittene.",
        "Besonders praktisch ist die Schaltfläche „Format“: Damit lässt sich nicht nur " +
          "nach Text suchen, sondern nach Formatierung. Alle fett gesetzten Stellen " +
          "finden und in eine Formatvorlage überführen? Genau dafür. Über „Sonderformat“ " +
          "erreichst du außerdem Absatzmarken, Tabulatoren und manuelle Zeilenumbrüche – " +
          "der schnellste Weg, ein Dokument von den Behelfslösungen aus dem Absatz-Modul " +
          "zu befreien.",
      ],
      keyPoints: [
        "„Nur ganzes Wort suchen“ verhindert Treffer mitten in Wörtern.",
        "Über „Format“ lässt sich nach Formatierung suchen, nicht nur nach Text.",
        "„Sonderformat“ findet Absatzmarken (^p) und Tabulatoren (^t).",
      ],
      task: {
        instruction:
          "Probiere die Suche nach Sonderformaten aus: Suche mit Strg + H nach zwei " +
          "aufeinanderfolgenden Absatzmarken (^p^p) und ersetze sie durch eine einzelne " +
          "(^p). Danach darf dein Dokument keine Leerzeilen mehr enthalten.",
        hint: "Trage im Suchfeld ^p^p und im Ersetzenfeld ^p ein. Bei mehreren Leerzeilen hintereinander mehrfach ausführen.",
        check: async () => {
          const snapshot = await readDocument();
          const leerzeilen = snapshot.paragraphs.filter((absatz) => absatz.text.length === 0).length;

          return fromCriteria(
            [criterion(`Keine Leerzeilen im Dokument (gefunden: ${leerzeilen})`, leerzeilen === 0)],
            {
              success: "Damit lässt sich ein fremdes Dokument in Sekunden aufräumen.",
              failure: "Es sind noch Leerzeilen übrig.",
              hint: "Bei drei Leerzeilen hintereinander musst du „Alle ersetzen“ mehrfach ausführen, bis Word 0 Ersetzungen meldet.",
            },
          );
        },
      },
    },
  ],
};
